import type { SortDirection } from "@nasam/kernel";

export interface DriverAssignmentDriver {
  readonly id: string;
  readonly username: string;
  readonly nameEn: string;
  readonly nameAr: string;
  readonly avatarUrl: string | null;
}

export interface DriverAssignmentBranch {
  readonly id: string;
  readonly nameEn: string;
  readonly nameAr: string;
}

export interface DriverAssignmentAsset {
  readonly id: string;
  readonly nameEn: string;
  readonly nameAr: string;
  readonly code: string;
  readonly branch: DriverAssignmentBranch;
}

export interface DriverAssignment {
  readonly id: string;
  readonly driver: DriverAssignmentDriver;
  readonly asset: DriverAssignmentAsset;
  /** ISO 8601 datetime the driver was assigned to the asset. */
  readonly assignedAt: string;
}

/** One asset the driver currently operates, as listed on the details screen. */
export interface DriverAssignmentAssetEntry {
  /** The assignment row this asset comes from. */
  readonly assignmentId: string;
  readonly asset: DriverAssignmentAsset;
  readonly assignedAt: string;
}

/** The driver behind an assignment, with every asset they currently operate. */
export interface DriverAssignmentDetails {
  readonly id: string;
  readonly driver: DriverAssignmentDriver;
  readonly assets: readonly DriverAssignmentAssetEntry[];
}

/** Name columns sort in the UI language, so each has an ar and en key. */
export type DriverAssignmentSortBy =
  | "driverNameEn"
  | "driverNameAr"
  | "assetNameEn"
  | "assetNameAr"
  | "assignedAt";

export interface DriverAssignmentsListQuery {
  readonly page?: number;
  readonly limit?: number;
  readonly sort?: SortDirection;
  readonly sortBy?: DriverAssignmentSortBy;
  /** Matches the driver name or the asset name / code. */
  readonly search?: string;
  /** ISO datetime bounds on `assignedAt`; set together (one range picker). */
  readonly fromAssignedAt?: string;
  readonly toAssignedAt?: string;
}

export interface DriverAssignmentAssetsQuery {
  /** Narrows the assets to one branch (Create form's Branch filter). */
  readonly branchId?: string;
}

/** The assignment date is set by the system, so only the pair is sent. */
export interface DriverAssignmentCreateInput {
  readonly driverId: string;
  readonly assetId: string;
}

/** What a create or update returns. */
export interface DriverAssignmentSaveResult {
  readonly assignment: DriverAssignment;
  /** The backend's own confirmation, already in the user's language
   *  (x-lang); null when it sends none and the screen uses its own copy. */
  readonly message: string | null;
}

/** What a delete returns. */
export interface DriverAssignmentDeleteResult {
  /** The backend's own confirmation (x-lang); null when it sends none. */
  readonly message: string | null;
}

/** Update sends the full pair; either side (or both) may change. */
export type DriverAssignmentUpdateInput = DriverAssignmentCreateInput;

/**
 * `HttpError.code`s the mock puts on its 409s so the form can mark the field.
 * The real API sends only a translated `message` (no code), so a real 409
 * shows that message in the error toast instead.
 */
export const DRIVER_ALREADY_ASSIGNED = "DRIVER_ALREADY_ASSIGNED";
export const ASSET_ALREADY_ASSIGNED = "ASSET_ALREADY_ASSIGNED";

/** Same values as Supervisor Assignment history (History story). */
export type DriverAssignmentHistoryAction = "Created" | "Updated" | "Removed";

/** Name columns sort in the UI language, like the list. */
export type DriverAssignmentHistorySortBy =
  | DriverAssignmentSortBy
  | "actionAt"
  | "action";

export interface DriverAssignmentHistoryQuery {
  readonly page?: number;
  readonly limit?: number;
  readonly sort?: SortDirection;
  readonly sortBy?: DriverAssignmentHistorySortBy;
  /** Matches the driver name, the asset name / code, or the action. */
  readonly search?: string;
  /** Only rows with this action; omitted means every action. */
  readonly action?: DriverAssignmentHistoryAction;
  /** Inclusive ISO datetime bounds; each pair is set together. */
  readonly fromAssignedAt?: string;
  readonly toAssignedAt?: string;
  readonly fromActionAt?: string;
  readonly toActionAt?: string;
}

/** One immutable history row, written by the system on every create,
 *  update and delete (History story, scenarios 6 and 7). */
/** History rows carry only the asset's id and names (no code or branch). */
export interface DriverAssignmentHistoryAsset {
  readonly id: string;
  readonly nameEn: string;
  readonly nameAr: string;
}

export interface DriverAssignmentHistoryItem {
  readonly id: string;
  /** The history API sends the driver's names only, no username. */
  readonly driver: Omit<DriverAssignmentDriver, "username">;
  readonly asset: DriverAssignmentHistoryAsset;
  readonly action: DriverAssignmentHistoryAction;
  /** When this driver/asset pair was assigned. */
  readonly assignedAt: string;
  /** When the action happened. */
  readonly actionAt: string;
}
